import { useMemo } from 'react';
import { AllowedExperimentKey } from '@tenants/types';
import { useTenantId } from 'hooks/use-tenant-id';
import { useExperimentContext } from 'contexts/growthbook/growthbook-provider';
import { isExperimentEnabledForTenant as checkTenantConfig, applySSRResult } from 'services/experiment-circuit-breaker';
import { ExperimentLogger } from 'observability/services/experiment-logger';

/**
 * Hook to determine if an experiment variation should be shown.
 * 
 * Uses SSR-evaluated experiment results which already include:
 * - Tenant config check (circuit breaker Layer 1)
 * - GrowthBook payload condition check (circuit breaker Layer 2)
 * - GrowthBook evaluation with URL/header overrides (circuit breaker Layer 3)
 * 
 * ## Client-Side Logging
 * 
 * This hook logs all experiment information to the browser console by "piping"
 * SSR metadata through the GrowthBook context. This ensures:
 * - All logs are visible in browser DevTools (not hidden in SSR)
 * - Complete context including override information
 * - Single source of truth for debugging
 * 
 * The logging sequence is:
 * 1. SSR Payload - What GrowthBook decided during SSR
 * 2. Override (if applied) - URL/header override that was used
 * 3. Final Decision - The final variation decision with all context
 * 
 * @param experimentKey - The experiment key to check
 * @returns Object with showVariation boolean and variationId (0 or 1)
 */
export function useExperimentVariation(
  experimentKey: AllowedExperimentKey
): { showVariation: boolean; variationId: number; isExperimentEnabledForTenant: boolean } {
  const tenantId = useTenantId();
  const { isInVariation, ssrMetadata } = useExperimentContext();

  // Check if experiment is enabled in tenant config
  const isExperimentEnabledForTenant = useMemo(
    () => checkTenantConfig(experimentKey, tenantId),
    [experimentKey, tenantId]
  );

  // Get SSR result from GrowthBook (already includes URL/header overrides)
  const ssrResult = useMemo(
    () => isInVariation(experimentKey),
    [isInVariation, experimentKey]
  );

  // Apply SSR result with tenant config check
  const { showVariation, variationId } = useMemo(() => {
    const result = applySSRResult(ssrResult, isExperimentEnabledForTenant);
    
    // Get SSR metadata for this experiment (piped from server)
    const metadata = ssrMetadata[experimentKey];
    
    // === CLIENT-SIDE LOGGING ===
    // Log all experiment information in the browser console.
    // Metadata is "piped" from SSR through GrowthBook context to provide
    // complete context including override information.
    
    if (metadata) {
      // 1. Log SSR Payload (what GrowthBook decided during SSR)
      ExperimentLogger.ssrPayload(
        experimentKey,
        metadata.variationId,
        metadata.inExperiment,
        tenantId
      );
      
      // 2. Log Override (if one was applied during SSR)
      if (metadata.overrideApplied && metadata.overrideSource) {
        ExperimentLogger.override(
          experimentKey,
          metadata.overrideSource,
          metadata.variationId,
          tenantId
        );
      }
    }
    
    // 3. Log Final Decision (with override context)
    ExperimentLogger.finalDecision(
      experimentKey,
      result.allowed,
      result.variationId,
      tenantId,
      metadata?.overrideApplied
    );
    
    return { showVariation: result.allowed, variationId: result.variationId };
  }, [experimentKey, isExperimentEnabledForTenant, ssrResult, tenantId, ssrMetadata]);

  return { showVariation, variationId, isExperimentEnabledForTenant };
}
